<template>
  <Teleport to="body">
    <Transition name="seeker-modal-fade">
      <div v-if="visible" class="seeker-modal-overlay" @click.self="close">
        <div class="seeker-modal">
          <button class="seeker-modal-close" @click="close" aria-label="Close">×</button>

          <div v-if="loading" class="seeker-modal-loading">Gazing into the mirror…</div>

          <template v-else-if="data">
            <!-- Tabs at the top -->
            <div class="sm-tabs">
              <button :class="['sm-tab', { active: tab === 'about' }]" @click="tab = 'about'">About{{ data && data.nick_name ? ' ' + data.nick_name : '' }}</button>
              <button :class="['sm-tab', { active: tab === 'shared' }]" @click="tab = 'shared'; readingPreview = null">Shared Readings</button>
            </div>

            <div class="sm-body">
              <!-- ABOUT — identity + the seeker's own public expression -->
              <div v-if="tab === 'about'">
                <div class="sm-identity">
                  <div class="sm-avatar">
                    <img v-if="data.avatar_url || data.evaluation_image_url" :src="data.avatar_url || data.evaluation_image_url" :alt="data.nick_name" />
                    <div v-else class="sm-avatar-ph"></div>
                  </div>
                  <div class="sm-id-info">
                    <h2 class="sm-name">{{ data.nick_name }}</h2>
                    <div class="sm-meta" v-if="data.date_joined">
                      <span>Seeker since {{ joinDate }}</span>
                    </div>
                    <div class="sm-stats">
                      <span><b>{{ data.stats?.readings_taken || 0 }}</b> readings</span>
                      <span><b>{{ data.stats?.readings_shared || 0 }}</b> shared</span>
                    </div>
                    <div class="sm-friend" v-if="data.friend_status && data.friend_status !== 'self'">
                      <button v-if="data.friend_status === 'none'" class="sm-friend-btn" @click="requestFriend">+ Add Friend</button>
                      <button v-else-if="data.friend_status === 'pending_outgoing'" class="sm-friend-btn ghost" disabled>Requested</button>
                      <button v-else-if="data.friend_status === 'pending_incoming'" class="sm-friend-btn" @click="acceptFriend">Accept Request</button>
                      <button v-else-if="data.friend_status === 'friends'" class="sm-friend-btn ghost" disabled>✓ Kindred</button>
                    </div>
                  </div>
                </div>

                <!-- Sub-tabs between the two rules: Detail | Compatibility -->
                <div class="sm-about-subtabs">
                  <button :class="['sm-subtab', { active: aboutSub === 'detail' }]" @click="aboutSub = 'detail'">Detail</button>
                  <button v-if="showCompat" :class="['sm-subtab', { active: aboutSub === 'compat' }]" @click="aboutSub = 'compat'">Compatibility</button>
                  <!-- Only when the seeker has explicitly shared their letter. -->
                  <button v-if="data.evaluation" :class="['sm-subtab', { active: aboutSub === 'letter' }]" @click="aboutSub = 'letter'">A Letter from Mystic AIda</button>
                </div>

                <!-- DETAIL — their signs, altar & affirmation -->
                <div v-show="aboutSub === 'detail'">
                  <!-- Sun & Rising — always shown (default glyph until a custom image
                       is painted). Sun is featured larger; custom images enlarge on click. -->
                  <div v-if="signList.length" class="sm-signs">
                    <div v-for="s in signList" :key="s.role" class="sm-sign-card" :class="{ 'is-sun': s.which === 'sun' }">
                      <button v-if="s.img" class="sm-sign-visual is-img" :title="`${s.value} ${s.role} - click to enlarge`" @click="lightbox = s.img">
                        <img :src="s.img" :alt="`${s.value} ${s.role}`" />
                      </button>
                      <span v-else class="sm-sign-visual is-chip"><span class="sm-glyph">{{ glyph(s.value) }}</span></span>
                      <div class="sm-sign-text">
                        <span class="sm-sign-name">{{ s.value }}</span>
                        <span class="sm-sign-role">{{ s.role }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Where the signs meet (public only: the seeker's "Show on your
                       Profile" switch; the server sends null when it is off). -->
                  <p v-if="data.mirror && data.mirror.about" class="sm-signs-about" data-testid="sm-signs-about">{{ data.mirror.about }}</p>

                  <!-- Affirmation (public only) -->
                  <div v-if="data.mirror && data.mirror.affirmation" class="sm-mirror-block">
                    <span class="sm-section-label">Affirmation</span>
                    <p class="sm-affirmation">“{{ data.mirror.affirmation }}”</p>
                  </div>

                  <!-- Altar (public only) -->
                  <div v-if="data.mirror && data.mirror.altar_image" class="sm-mirror-block">
                    <span class="sm-section-label">Their Altar</span>
                    <img :src="data.mirror.altar_image" class="sm-altar-img" @click="lightbox = data.mirror.altar_image" />
                  </div>

                  <p v-if="!signList.length && !(data.mirror && (data.mirror.affirmation || data.mirror.altar_image || data.mirror.about))" class="sm-stub">{{ data.nick_name }} hasn't shared any details yet.</p>
                </div>

                <!-- COMPATIBILITY (full synastry) -->
                <div v-show="aboutSub === 'compat'" v-if="showCompat" class="sm-compat-section">
                  <div v-if="data.compatibility.reason === 'viewer_no_birthdate'" class="sm-compat-block">
                    <span class="sm-section-label">✦ Cosmic Compatibility</span>
                    <p class="sm-compat-teaser">Add your birth date to unlock compatibility - we read your full chart against theirs.</p>
                    <button class="sm-compat-btn" @click="goAddBirth">Add my birth details</button>
                  </div>
                  <div v-else-if="data.compatibility.reason === 'other_no_birthdate'" class="sm-mirror-block">
                    <span class="sm-section-label">✦ Cosmic Compatibility</span>
                    <p class="sm-compat-teaser">{{ data.nick_name }} hasn't added their birth date yet - compatibility needs both charts.</p>
                  </div>
                  <!-- The same "You & <name>" as the gift room (recipient polish
                       §19): the blank picture until seen, then one scroll. No
                       /100, no meter bars (decision 9); the data stays stored. -->
                  <div v-else-if="!data.compatibility.revealed" class="sm-compat-locked">
                    <YouAndThem :view="compatView" context="community" blank>
                      <p class="sm-compat-teaser">See the three threads between your sky and {{ data.nick_name }}’s, in plain words, with one small thing to try for each.</p>
                      <button class="sm-compat-btn" data-testid="sm-see" :disabled="revealing" @click="revealCompat">
                        <BreathDots v-if="revealing" label="Reading the two skies…" />
                        <template v-else>✦ See you &amp; {{ data.nick_name }} · ✦ {{ compatCost }}</template>
                      </button>
                      <p class="sm-compat-err" v-if="compatError">{{ compatError }}</p>
                    </YouAndThem>
                  </div>
                  <div v-else class="sm-compat-result">
                    <YouAndThem :view="compatView" context="community" :asked="compatAsked" :saving-time="savingTime"
                                :time-errors="timeErrors" @ask-time="askTheirTime" @save-time="saveMyTime" />
                  </div>
                </div>

                <!-- A LETTER FROM MYSTIC AIDA -->
                <div v-show="aboutSub === 'letter'">
                  <div v-if="data.evaluation" class="sm-eval">
                    <p class="sm-eval-text">{{ data.evaluation.text }}</p>
                    <span class="sm-eval-date" v-if="data.evaluation.date">Written {{ evalDate }}</span>
                  </div>
                  <div v-else class="sm-stub">
                    <p v-if="data.has_evaluation">This seeker keeps Mystic AIda's letter private.</p>
                    <p v-else>Mystic AIda hasn't written this seeker a letter yet - it comes after 10 readings.</p>
                  </div>
                </div>
              </div>

              <!-- SHARED READINGS -->
              <div v-else-if="tab === 'shared'">
                <!-- Inline reading view -->
                <div v-if="readingPreview" class="sm-reading">
                  <!-- Coven banner — pronounced, at the very top: you're seeing the original. -->
                  <div v-if="isCoven" class="sm-coven-banner">✦ Coven · The Original Reading</div>

                  <!-- The subject, held above every step. Seeking → the question they asked;
                       Be Inspired → the realization (what's inspirational isn't the question). -->
                  <h3 v-if="readingTitle" class="sm-reading-question">{{ readingTitle }}</h3>

                  <!-- COVEN: the querent's ORIGINAL reading, in the real session layout. -->
                  <template v-if="isCoven">
                    <SpreadReading
                      :question="''"
                      :title="''"
                      :cards="originalCards"
                      :synthesis="readingPreview.reading_summary || ''"
                      :image="readingPreview.image_url || ''"
                      :gate-from="-1"
                      :locked="false"
                      :reveal-summary="false"
                      :zoomable="true"
                      @zoom="lightbox = $event"
                      @card-detail="openCardDetail($event)"
                    />
                    <p class="sm-read-credit sm-read-credit-row">
                      <span class="sm-read-credit-eyebrow">Read by</span>
                      <a @click.stop="openReaderHome(readingPreview.reader_subdomain)">{{ readingPreview.reader_name }}</a>
                    </p>
                    <!-- Append the resonance rating: someone who knows you confirming this rings true. -->
                    <div class="sm-coven-actions">
                      <div class="sm-reading-actions sm-reading-actions-center">
                        <button class="sm-react" :class="{ on: readingPreview.supported }" @click="toggleReadingReaction">
                          ✦ {{ readingPreview.intent === 'seeking' ? 'Send light' : 'Inspired' }}<span v-if="readingPreview.support_count"> · {{ readingPreview.support_count }}</span>
                        </button>
                        <button class="sm-react" :class="{ on: showComposer }" @click="toggleComposer">
                          {{ readingPreview.intent === 'seeking' ? 'Words of Support' : 'Add a Response' }}
                        </button>
                      </div>
                      <RatingCrystals :reading-id="readingPreview.short_uuid" />
                    </div>
                  </template>

                  <!-- PUBLIC, now its lesson (community-lesson DESIGN.md §5, §7 rule 7):
                       the lesson letter and "✦ This spoke to me", no replies. -->
                  <PublicLetter v-else-if="readingPreview.public_version" :lesson="readingPreview.public_version" :mark="false" data-testid="seeker-lesson">
                    <div class="sm-reading-actions sm-reading-actions-center sm-lesson-actions">
                      <button class="sm-react" :class="{ on: readingPreview.supported }" data-testid="seeker-lesson-spoke" @click="toggleReadingReaction">
                        ✦ This spoke to me<span v-if="readingPreview.support_count"> · {{ readingPreview.support_count }}</span>
                      </button>
                    </div>
                  </PublicLetter>
                  <!-- PUBLIC: the short lens (avatar + words · image · ‹step› nav + actions). -->
                  <SharedReadingLens
                    v-else
                    :cards="lensCards"
                    :moral="lensMoral"
                    :caption="readingPreview.takeaway || ''"
                    :illustration="readingPreview.image_url || ''"
                    :avatar="data.avatar_url || ''"
                    @zoom="lightbox = $event"
                    @card-detail="openCardDetail($event)"
                  >
                    <template #credit>
                      <p class="sm-read-credit">
                        <span class="sm-read-credit-eyebrow">The Moral By:</span>
                        <a @click.stop="openReaderHome(readingPreview.reader_subdomain)">{{ readingPreview.reader_name }}</a>
                      </p>
                    </template>
                    <template #actions>
                      <div class="sm-reading-actions">
                        <button class="sm-react" :class="{ on: readingPreview.supported }" @click="toggleReadingReaction">
                          ✦ {{ readingPreview.intent === 'seeking' ? 'Send light' : 'Inspired' }}<span v-if="readingPreview.support_count"> · {{ readingPreview.support_count }}</span>
                        </button>
                        <button class="sm-react" :class="{ on: showComposer }" @click="toggleComposer">
                          {{ readingPreview.intent === 'seeking' ? 'Words of Support' : 'Add a Response' }}
                        </button>
                      </div>
                    </template>
                  </SharedReadingLens>

                  <!-- The Circle response layer — revealed by the "Give Words of Support" toggle. -->
                  <div v-if="showComposer && !readingPreview.public_version" class="sm-graces">
                    <span class="sm-section-label">{{ readingPreview.intent === 'seeking' ? '✦ Words of Support' : '✦ Responses' }}</span>
                    <div v-if="readingPreview.graces && readingPreview.graces.length" class="sm-grace-list">
                      <div v-for="(g, i) in readingPreview.graces" :key="i" class="sm-grace">
                        <div class="sm-grace-avatar">
                          <img v-if="g.author_avatar" :src="g.author_avatar" :alt="g.author_nick" />
                          <div v-else class="sm-grace-ph"></div>
                        </div>
                        <div class="sm-grace-body">
                          <span class="sm-grace-by">{{ g.author_nick }}<span v-if="g.date"> · {{ g.date }}</span></span>
                          <p class="sm-grace-text">{{ g.text }}</p>
                        </div>
                      </div>
                    </div>
                    <div v-if="store.querent_id" class="sm-grace-composer">
                      <textarea v-model="graceDraft" rows="2" @input="graceError = ''"
                                :placeholder="readingPreview.intent === 'seeking' ? 'Offer them support - a blessing, or what got you through…' : 'Share what this stirred in you…'"></textarea>
                      <p v-if="graceError" class="sm-grace-pushback">{{ graceError }}</p>
                      <button class="sm-grace-send" :disabled="!graceDraft.trim() || gracePosting" @click="postGrace">
                        {{ gracePosting ? 'Sending…' : (graceError ? 'Try again' : (readingPreview.intent === 'seeking' ? 'Send support' : 'Respond')) }}
                      </button>
                    </div>
                  </div>
                </div>

                <div v-else-if="readingLoading" class="sm-stub">Reading the cards…</div>

                <div v-else-if="data.shared_readings && data.shared_readings.length" class="sm-shared-grid">
                  <div v-for="(r, i) in data.shared_readings" :key="i" class="sm-shared-card" @click="openReading(r.short_uuid)">
                    <!-- The picture opens full size with its caption (picture-caption r1); the rest of the tile opens the lesson. -->
                    <PictureOpen v-if="r.image_url" :src="r.image_url" :caption="captionOf(r)" small />
                    <div v-else class="sm-shared-ph"></div>
                    <div class="sm-shared-overlay"><h4>{{ r.summary_title }}</h4></div>
                  </div>
                </div>
                <p v-else class="sm-stub">This seeker hasn't shared any readings yet.</p>
              </div>

            </div>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>

  <!-- Fullscreen image lightbox — teleported on its OWN so the modal's transition
       wrapper can't confine its fixed positioning (it was rendering inline at top). -->
  <Teleport to="body">
    <div v-if="lightbox" class="seeker-img-overlay" @click="lightbox = null">
      <img :src="lightbox" />
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { formatAmount } from '@/utils/carats'
import { requestAuth } from '@/utils/request-auth'
import { store } from '@/store'
import { getSeekerDetail, getReadingPreview, sendFriendRequest, respondFriendRequest, revealCompatibility, offerReadingGrace, toggleReadingSupport, askBirthTime, saveBirthMoment } from '@/api-service'
import YouAndThem from '@/components/ui/YouAndThem.vue'
import BreathDots from '@/components/ui/BreathDots.vue'
import { youAndThem } from '@/utils/you-and-them'
import SharedReadingLens from '@/components/ui/games/SharedReadingLens.vue'
import SpreadReading from '@/components/ui/games/SpreadReading.vue'
import RatingCrystals from '@/components/ui/RatingCrystals.vue'
import { showCardDetail } from '@/utils/card-detail'
import PublicLetter from '@/components/ui/share/PublicLetter.vue'
import { track } from '@/utils/analytics'
import { openMystic, captionOf } from '@/utils/lesson'
import PictureOpen from '@/components/ui/share/PictureOpen.vue'

const visible = ref(false)
const loading = ref(false)
const data = ref(null)
const tab = ref('about')
const readingPreview = ref(null)
const readingLoading = ref(false)
const activeCard = ref(null)
const graceDraft = ref('')
const gracePosting = ref(false)
const graceError = ref('')
const showComposer = ref(false)
// The handle this profile was opened by (name_slug or nick_name): what a
// seeker report points at, since the server looks it up the same way.
const openedAs = ref('')
const isSelf = computed(() => !!(data.value && data.value.friend_status === 'self'))
const lightbox = ref(null)
const revealing = ref(false)
const compatError = ref('')
const aboutSub = ref('detail')   // About sub-tab: 'detail' | 'compat'
const showCompat = computed(() =>
  !!store.querent_id && !!(data.value && data.value.compatibility) && (data.value.friend_status !== 'self'))
// "You & <name>" (recipient polish §19): the shared view model.
const compatView = computed(() => youAndThem((data.value && data.value.compatibility) || {},
  { other: (data.value && data.value.nick_name) || '' }))
// The server sends the price on every reply (10, editable in MCP); 10 is what it charges.
const compatCost = computed(() => {
  const c = data.value && data.value.compatibility && data.value.compatibility.cost
  return c === 0 || c ? c : 10
})
const compatAsked = ref(false)
const savingTime = ref(false)
const timeErrors = ref(null)
async function askTheirTime() {
  if (!data.value) return
  try {
    const r = await askBirthTime({ nickName: data.value.nick_name })
    if (r && r.ok !== false) compatAsked.value = true
  } catch (e) { /* nothing was sent; the button stays */ }
}
// My own time and place, right here: the next read remakes the row (R6.9).
async function saveMyTime(out) {
  savingTime.value = true; timeErrors.value = null
  const body = { ...(out || {}) }
  delete body.form; delete body.legacy
  try {
    await saveBirthMoment(body)
    const fresh = await getSeekerDetail(openedAs.value || data.value.nick_name, store.session_id)
    if (fresh) data.value = fresh
  } catch (e) {
    timeErrors.value = (e && e.response && e.response.data && e.response.data.errors) || { _: 'That did not save. Try again.' }
  } finally { savingTime.value = false }
}
function goAddBirth() {
  close()
  window.dispatchEvent(new CustomEvent('seeker-add-birth'))
}

const sunSign = computed(() => data.value?.sun_sign || data.value?.mirror?.chart?.sun || '')
const risingSign = computed(() => data.value?.mirror?.chart?.rising || '')

const ZODIAC_GLYPHS = {
  Aries: '♈', Taurus: '♉', Gemini: '♊', Cancer: '♋', Leo: '♌', Virgo: '♍',
  Libra: '♎', Scorpio: '♏', Sagittarius: '♐', Capricorn: '♑', Aquarius: '♒', Pisces: '♓',
}
function glyph(s) { return ZODIAC_GLYPHS[s] || '✦' }

// Sun + Rising — always shown (default glyph until a custom image is painted).
// Sun first so it can be featured larger; only custom images are clickable.
const signList = computed(() => {
  const sg = data.value?.mirror?.signs || {}
  return [
    { which: 'sun', role: 'Sun', value: sunSign.value, img: sg.sun },
    { which: 'rising', role: 'Rising', value: risingSign.value, img: sg.rising },
  ].filter(s => s.value)
})

const joinDate = computed(() => {
  if (!data.value?.date_joined) return ''
  return new Date(data.value.date_joined).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
})
const evalDate = computed(() => {
  if (!data.value?.evaluation?.date) return ''
  return new Date(data.value.evaluation.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
})

async function openSeeker(nickName, shortUuid = null) {
  if (!nickName) return
  openedAs.value = nickName
  visible.value = true
  loading.value = true
  tab.value = shortUuid ? 'shared' : 'about'
  aboutSub.value = 'detail'
  compatAsked.value = false
  readingPreview.value = null
  data.value = null
  data.value = await getSeekerDetail(nickName, store.session_id)
  loading.value = false
  if (shortUuid) openReading(shortUuid)
}

function close() {
  visible.value = false
  readingPreview.value = null
  data.value = null
}

// The reading's title/subject is intent-aware: a 'seeking' reading leads with the
// question (the struggle); a 'be inspired' reading leads with the realization
// ("{Seeker} is {headline}") — the question isn't what's inspirational.
const readingTitle = computed(() => {
  const rp = readingPreview.value
  if (!rp) return ''
  // A lesson carries its own heading, and no "<name> is …" (§9).
  if (rp.public_version) return ''
  if (rp.intent === 'offering' && rp.headline) {
    return `${data.value?.nick_name || 'This seeker'} is ${rp.headline}`
  }
  return rp.question || ''
})

// Coven members (and the owner) see the ORIGINAL reading; everyone else the lens.
const isCoven = computed(() => !!readingPreview.value?.viewer_in_coven)

// Map the shared reading's cards into the lens shape — the SHORT public-facing
// meaning per card, OR the full session text for coven viewers (the original).
const lensCards = computed(() => {
  const cards = readingPreview.value?.cards || []
  const coven = isCoven.value
  return cards.map((c, i) => {
    let pos = (c.reference || '').replace(/reading/ig, '').replace(/_/g, ' ').trim()
    pos = pos ? pos.charAt(0).toUpperCase() + pos.slice(1) : `Card ${i + 1}`
    return { position: pos, name: c.name, image: c.image_url || '', reversed: !!c.reversed,
             meaning: coven ? (c.reading_text || c.lens_meaning || '') : (c.lens_meaning || c.tagline || c.reading_text || '') }
  })
})

// The summary text: the full reading for coven viewers, the distilled moral otherwise.
const lensMoral = computed(() => {
  const rp = readingPreview.value
  if (!rp) return ''
  return isCoven.value ? (rp.reading_summary || rp.moral || '') : (rp.moral || '')
})

// A card was clicked in the coven reading → open the layered detail modal with
// the larger image + this card's meaning and the reader's symbolism.
function openCardDetail(index) {
  const c = (readingPreview.value?.cards || [])[index]
  if (!c) return
  let pos = (c.reference || '').replace(/reading/ig, '').replace(/_/g, ' ').trim()
  pos = pos ? pos.charAt(0).toUpperCase() + pos.slice(1) : ''
  showCardDetail({
    name: c.name, position: pos, image_url: c.image_url || '', reversed: !!c.reversed,
    card_meaning: c.card_meaning || '', reader_symbolism: c.reader_symbolism || [],
  })
}

// Coven view renders the querent's ORIGINAL reading in the real session layout:
// full per-card interpretation (reading_text), not the trimmed lens line.
const originalCards = computed(() => {
  const cards = readingPreview.value?.cards || []
  return cards.map((c, i) => {
    let pos = (c.reference || '').replace(/reading/ig, '').replace(/_/g, ' ').trim()
    pos = pos ? pos.charAt(0).toUpperCase() + pos.slice(1) : `Card ${i + 1}`
    return { position: pos, name: c.name, image: c.image_url || '', reversed: !!c.reversed,
             meaning: c.reading_text || c.lens_meaning || c.tagline || '' }
  })
})

async function toggleReadingReaction() {
  if (!readingPreview.value) return
  // A lesson is answered signed out too (one per browser session, §5).
  const isLesson = !!readingPreview.value.public_version
  if (!store.querent_id && !isLesson) { requestAuth('social'); return }
  const r = await toggleReadingSupport(readingPreview.value.short_uuid)
  if (r && r.ok) {
    const on = r.spoke !== undefined ? r.spoke : r.supported
    const n = r.spoke_count !== undefined ? r.spoke_count : r.support_count
    readingPreview.value = { ...readingPreview.value, supported: on, support_count: n }
    if (isLesson && on) track('lesson_resonated', { item_id: readingPreview.value.short_uuid })
  }
}

async function postGrace() {
  if (!graceDraft.value.trim() || gracePosting.value || !readingPreview.value) return
  gracePosting.value = true
  const r = await offerReadingGrace(readingPreview.value.short_uuid, graceDraft.value.trim())
  if (r && r.ok) {
    readingPreview.value = { ...readingPreview.value, graces: r.graces }
    graceDraft.value = ''
    graceError.value = ''
    showComposer.value = false
  } else if (r && r.reason === 'unkind') {
    // Kindness gate pushed it back — keep their text, invite a rephrase.
    graceError.value = r.message || "That didn't quite read as kind - try rephrasing."
  }
  gracePosting.value = false
}

async function openReading(shortUuid) {
  readingLoading.value = true
  readingPreview.value = null
  activeCard.value = null
  graceDraft.value = ''
  graceError.value = ''
  showComposer.value = false
  lightbox.value = null
  try {
    readingPreview.value = await getReadingPreview(shortUuid)
    activeCard.value = 0
  } catch (e) {
    console.error('Failed to load reading preview:', e)
  } finally {
    readingLoading.value = false
  }
}

async function revealCompat() {
  if (revealing.value) return
  if (!data.value || !data.value.nick_name) return   // profile not loaded: nothing to reveal against
  revealing.value = true
  compatError.value = ''
  let r = null
  try {
    r = await revealCompatibility(data.value.nick_name, store.session_id)
  } catch (e) {
    r = null
  } finally {
    revealing.value = false
  }
  if (!r || !r.ok) {
    // The server sends `cost`; typing the number here is how it went stale
    // (said 5, charged 10) and how it would go stale again.
    if (r && r.reason === 'insufficient') {
      compatError.value = `Not enough carats - ${formatAmount(r.cost || 0)} to reveal.`
    }
    else if (r && r.reason === 'needs_chart') compatError.value = 'You both need birth details for this.'
    else compatError.value = 'Could not reveal right now.'
    return
  }
  data.value.compatibility = {
    ...data.value.compatibility, revealed: true,
    score: r.score, report: r.report, meters: r.meters,
    meter_labels: r.meter_labels, aspects: r.aspects, time_warning: r.time_warning,
    charts: r.charts, you_and: r.you_and, ...(r.cost !== undefined ? { cost: r.cost } : {}),
  }
  if (typeof r.balance === 'number') store.querent_carat_balance = r.balance
}

// Toggle the response composer (gated on auth, like friend requests).
function toggleComposer() {
  if (!store.querent_id) { requestAuth('social'); return }
  showComposer.value = !showComposer.value
}

// Reader credit → that Mystic's room, in this tab (never link out mid-session,
// mystic-page OUTLINE decision 10). The profile closes so the room is in view.
function openReaderHome(subdomain) {
  if (!subdomain) return
  close()
  openMystic(subdomain, 'seeker')
}

async function requestFriend() {
  if (!store.querent_id) {
    requestAuth('social', { action: 'request-friend',
      args: { querentUuid: data.value && data.value.querent_uuid } })
    return
  }
  try {
    const r = await sendFriendRequest(data.value.querent_uuid, store.session_id)
    if (r && r.status) data.value.friend_status = r.status
  } catch (e) { console.error('Friend request failed:', e) }
}
async function acceptFriend() {
  if (!store.querent_id) {
    requestAuth('social', { action: 'accept-friend',
      args: { querentUuid: data.value && data.value.querent_uuid } })
    return
  }
  const alias = (window.prompt('Add a nickname for this friend (optional):') || '').trim()
  try {
    const r = await respondFriendRequest(data.value.querent_uuid, 'accept', store.session_id, alias)
    if (r && r.status) data.value.friend_status = r.status
  } catch (e) { console.error('Accept friend failed:', e) }
}

function onOpenEvent(e) { openSeeker(e.detail?.nickName, e.detail?.shortUuid) }
function onKey(e) { if (e.key === 'Escape' && visible.value) close() }

onMounted(() => {
  window.addEventListener('open-seeker-modal', onOpenEvent)
  window.addEventListener('keydown', onKey)
})
onUnmounted(() => {
  window.removeEventListener('open-seeker-modal', onOpenEvent)
  window.removeEventListener('keydown', onKey)
})
</script>

<style scoped>
.seeker-modal-overlay { position: fixed; inset: 0; z-index: 9000; background: rgba(6,2,14,0.82); backdrop-filter: blur(4px); display: flex; align-items: flex-start; justify-content: center; padding: 48px 16px; overflow-y: auto; }
.seeker-modal { position: relative; width: 100%; max-width: 680px; background: linear-gradient(180deg,#1a1030,#120a24); border: 1px solid rgba(160,120,220,0.2); border-radius: 18px; box-shadow: 0 30px 80px -30px rgba(0,0,0,0.9); color: #E8D8FF; overflow: hidden; }
.seeker-modal-close { position: absolute; top: 12px; right: 14px; z-index: 3; width: 30px; height: 30px; border-radius: 50%; border: none; background: rgba(255,255,255,0.08); color: #E8D8FF; font-size: 20px; line-height: 1; cursor: pointer; transition: background 0.15s; }
.seeker-modal-close:hover { background: rgba(255,255,255,0.18); }
.seeker-modal-loading, .sm-stub { padding: 40px 24px; text-align: center; font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 16px; color: rgba(195,169,255,0.6); }

/* Identity block (top of the About tab) */
.sm-identity { display: flex; gap: 18px; align-items: center; margin-bottom: 4px; padding-bottom: 18px; border-bottom: 1px solid rgba(93,59,140,0.22); }
.sm-avatar { width: 128px; height: 128px; border-radius: 50%; overflow: hidden; border: 2px solid rgba(195,169,255,0.5); flex: none; }
.sm-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.sm-avatar-ph { width: 100%; height: 100%; background: rgba(93,59,140,0.35); }
.sm-id-info { min-width: 0; flex: 1; }
.sm-name { font-family: 'Cinzel', serif; font-size: 26px; color: #FFDFA3; margin: 0 0 4px; }
.sm-meta { font-size: 12px; color: rgba(195,169,255,0.6); margin-bottom: 8px; }
.sm-dot { margin: 0 5px; opacity: 0.6; }
.sm-stats { display: flex; gap: 16px; font-size: 13px; color: rgba(232,216,255,0.6); margin-bottom: 12px; }
.sm-stats b { color: #E8D8FF; font-weight: 600; }
.sm-friend-btn { font-family: 'Cinzel', serif; font-size: 12px; letter-spacing: 0.04em; color: #1a0f2e; background: linear-gradient(180deg,#FFDFA3,#cdae6a); border: none; border-radius: 9px; padding: 8px 18px; cursor: pointer; }
.sm-friend-btn.ghost { background: transparent; border: 1px solid rgba(195,169,255,0.3); color: rgba(195,169,255,0.7); cursor: default; }

/* Tabs */
.sm-tabs { display: flex; gap: 6px; padding: 20px 26px 0; border-bottom: 1px solid rgba(93,59,140,0.2); flex-wrap: wrap; }
.sm-tab { background: none; border: none; border-bottom: 2px solid transparent; color: rgba(195,169,255,0.5); font-family: 'Cinzel', serif; font-size: 14px; padding: 6px 4px 12px; margin-right: 18px; cursor: pointer; }
.sm-tab.active { color: #FFDFA3; border-bottom-color: #FFDFA3; }

.sm-body { padding: 22px 26px 28px; }

/* About */
.sm-teaser { font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 17px; color: #FFDFA3; line-height: 1.5; margin: 0 0 18px; }
.sm-eval-banner { width: 100%; height: 180px; border-radius: 12px; overflow: hidden; margin-bottom: 14px; border: 1px solid rgba(195,169,255,0.2); }
.sm-eval-banner img { width: 100%; height: 100%; object-fit: cover; display: block; }
.sm-eval-top { display: flex; gap: 16px; margin-bottom: 14px; }
.sm-eval-img { width: 110px; height: 110px; border-radius: 12px; overflow: hidden; flex: none; border: 1px solid rgba(195,169,255,0.25); }
.sm-eval-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
.sm-eval-title { font-family: 'Cinzel', serif; font-size: 18px; color: #FFDFA3; margin: 0 0 6px; }
.sm-eval-teaser { font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 14px; color: rgba(232,216,255,0.7); line-height: 1.5; margin: 0; }
.sm-eval-text { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 15px; color: rgba(255,255,255,0.55); line-height: 1.7; margin: 0 0 12px; white-space: pre-wrap; }
.sm-eval-date { font-size: 11px; color: rgba(195,169,255,0.45); }

/* Shared readings grid */
.sm-shared-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: 12px; }
.sm-shared-card { position: relative; aspect-ratio: 4/5; border-radius: 12px; overflow: hidden; cursor: pointer; border: 1px solid rgba(255,255,255,0.06); transition: transform 0.15s, border-color 0.2s; }
.sm-shared-card:hover { transform: translateY(-3px); border-color: rgba(255,223,163,0.4); }
.sm-shared-card img { width: 100%; height: 100%; object-fit: cover; display: block; }
.sm-shared-card .po { position: absolute; inset: 0; }
.sm-shared-ph { width: 100%; height: 100%; background: rgba(93,59,140,0.3); }
.sm-shared-overlay { position: absolute; left: 0; right: 0; bottom: 0; padding: 22px 12px 10px; background: linear-gradient(to top, rgba(10,0,21,0.9), transparent); }
.sm-shared-overlay h4 { font-family: 'Cinzel', serif; font-size: 12px; color: #fff; margin: 0; line-height: 1.3; }

/* Inline reading — the true question, held as the title above every step */
.sm-reading-question { font-family: 'Cinzel', serif; font-size: 19px; color: #FFDFA3; line-height: 1.35;
  text-align: center; margin: 0 0 20px; }
.sm-coven-banner { font-family: 'Cinzel', serif; font-size: 15px; letter-spacing: 0.1em; text-transform: uppercase;
  color: #FFDFA3; text-align: center; margin: 0 0 16px; padding: 10px 16px; border-radius: 10px;
  background: linear-gradient(180deg, rgba(240,217,163,0.16), rgba(240,217,163,0.05));
  border: 1px solid rgba(240,217,163,0.45); box-shadow: 0 4px 18px -8px rgba(240,217,163,0.3); }
.sm-read-credit-row { flex-direction: row; gap: 6px; align-items: baseline; justify-content: center; margin: 14px 0 0; }
/* Coven engagement: reaction + Words of Support, then the resonance rating beneath. */
.sm-coven-actions { display: flex; flex-direction: column; align-items: center; gap: 12px; margin-top: 18px;
  padding-top: 16px; border-top: 1px solid rgba(93,59,140,0.22); }
.sm-reading-actions-center { justify-content: center; }

/* Inline reading — "The Moral By:" credit, shown below the summary image */
.sm-read-credit { display: flex; flex-direction: column; align-items: center; gap: 2px; margin: 6px 0 0; text-align: center; }
.sm-read-credit-eyebrow { font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; color: rgba(195,169,255,0.6); }
.sm-read-credit a { font-family: 'Cinzel', serif; font-size: 14px; color: #C3A9FF; cursor: pointer; transition: color 0.18s ease; }
.sm-read-credit a:hover { color: #f0d9a3; }
.sm-reading-actions { display: flex; flex-direction: row; flex-wrap: nowrap; align-items: center; gap: 8px; flex: none; }
.sm-react { display: inline-flex; align-items: center; justify-content: center; gap: 4px; box-sizing: border-box;
  background: rgba(255,255,255,0.05); border: 1px solid rgba(195,169,255,0.3); color: rgba(232,216,255,0.85);
  font-family: 'Cinzel', serif; font-size: 13px; padding: 9px 18px; border-radius: 999px; cursor: pointer;
  transition: all 0.18s; white-space: nowrap; }
.sm-react:hover { border-color: rgba(255,223,163,0.55); color: #f0d9a3; }
.sm-react.on { background: rgba(240,217,163,0.16); border-color: rgba(240,217,163,0.6); color: #FFDFA3; }
.sm-reading-back { background: none; border: none; color: rgba(195,169,255,0.6); font-size: 13px; cursor: pointer; padding: 0 0 14px; }
.sm-reading-hero { position: relative; border-radius: 12px; overflow: hidden; cursor: pointer; margin-bottom: 14px; }
.sm-reading-hero img { width: 100%; display: block; }
.sm-reading-hero-by { position: absolute; left: 0; right: 0; bottom: 0; padding: 24px 14px 10px; background: linear-gradient(to top, rgba(10,0,21,0.9), transparent); font-size: 12px; color: rgba(232,216,255,0.8); }
.sm-reading-hero-by a { color: #C3A9FF; cursor: pointer; }
.sm-reading-context { font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 15px; color: rgba(255,223,163,0.6); line-height: 1.5; margin: 0 0 14px; }
.sm-reading-summary { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 15px; color: rgba(255,255,255,0.55); line-height: 1.65; margin: 0 0 16px; }
.sm-cards { display: flex; gap: 16px; flex-wrap: wrap; }
.sm-card-text { flex: 1; min-width: 200px; }
.sm-card-pos { font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; color: #FFDFA3; }
.sm-card-tagline { font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 15px; color: rgba(232,216,255,0.75); margin: 6px 0; }
.sm-card-body { font-size: 14px; color: rgba(255,255,255,0.55); line-height: 1.65; margin: 0; }
.sm-card-viewer { display: flex; align-items: center; gap: 8px; }
.sm-card-arrow { background: rgba(255,255,255,0.06); border: none; color: #E8D8FF; width: 30px; height: 44px; border-radius: 8px; font-size: 18px; cursor: pointer; }
.sm-card-arrow:disabled { opacity: 0.25; cursor: default; }
.sm-card-large { display: flex; flex-direction: column; align-items: center; gap: 6px; }
.sm-card-large-img { width: 120px; border-radius: 8px; overflow: hidden; border: 1px solid rgba(195,169,255,0.25); }
.sm-card-large-img.reversed img { transform: rotate(180deg); }
.sm-card-large-img img { width: 100%; display: block; }
.sm-card-large-ph { width: 120px; height: 200px; background: rgba(93,59,140,0.3); }
.sm-card-large-name { font-size: 11px; color: rgba(195,169,255,0.7); text-align: center; }

/* Report (Trust & safety) — a quiet link, right-aligned under what it is about. */

.sm-lesson-actions { padding: 6px 16px 18px; }
/* The Circle response layer — graces offered back on a reading. */
.sm-graces { margin-top: 22px; padding-top: 18px; border-top: 1px solid rgba(93,59,140,0.22); }
.sm-grace-list { display: flex; flex-direction: column; gap: 12px; margin: 12px 0 14px; }
.sm-grace { display: flex; gap: 11px; }
.sm-grace-avatar { width: 34px; height: 34px; border-radius: 50%; overflow: hidden; flex: none; border: 1px solid rgba(195,169,255,0.4); }
.sm-grace-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.sm-grace-ph { width: 100%; height: 100%; background: rgba(93,59,140,0.3); }
.sm-grace-body { min-width: 0; }
.sm-grace-by { font-size: 11.5px; color: rgba(195,169,255,0.6); }
.sm-grace-text { font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 15px; color: rgba(232,216,255,0.85); line-height: 1.5; margin: 2px 0 0; }
.sm-grace-empty { font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 14px; color: rgba(195,169,255,0.5); margin: 10px 0 14px; }
.sm-grace-composer { display: flex; flex-direction: column; gap: 8px; }
.sm-grace-pushback { font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 14px;
  color: rgba(255,180,160,0.9); margin: 0; line-height: 1.4; }
.sm-grace-composer textarea { width: 100%; box-sizing: border-box; background: rgba(255,255,255,0.04); border: 1px solid rgba(195,169,255,0.25);
  border-radius: 10px; padding: 10px 12px; color: #E8D8FF; font-family: inherit; font-size: 14px; resize: vertical; }
.sm-grace-composer textarea:focus { outline: none; border-color: rgba(255,223,163,0.5); }
.sm-grace-send { align-self: flex-end; font-family: 'Cinzel', serif; font-size: 12.5px; color: #1a0f2e;
  background: linear-gradient(180deg,#FFDFA3,#cdae6a); border: none; border-radius: 9px; padding: 8px 18px; cursor: pointer; }
.sm-grace-send:disabled { opacity: 0.5; cursor: default; }

/* Compatibility (synastry) — rolled into the About tab */
.sm-compat-section { margin-top: 22px; padding-top: 18px; border-top: 1px solid rgba(93,59,140,0.22); }
.sm-compat-section .sm-section-label { display: block; margin-bottom: 10px; }
.sm-compat-block, .sm-compat-locked { padding: 0; }
.sm-compat-teaser { font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 15px; color: rgba(232,216,255,0.78); margin: 0 0 12px; }
.sm-compat-warn { font-size: 12px; color: #e0b46a; margin: 0 0 12px; }
.sm-compat-btn { font-family: 'Cinzel', serif; letter-spacing: 0.04em; font-size: 13px; color: #1a0f2e; background: linear-gradient(180deg,#FFDFA3,#cdae6a); border: none; border-radius: 10px; padding: 11px 22px; cursor: pointer; }
.sm-compat-btn:disabled { opacity: 0.6; cursor: default; }
.sm-compat-err { font-size: 12px; color: #e08fb0; margin: 10px 0 0; }
/* Revealed */
.sm-compat-overall { display: flex; align-items: baseline; gap: 12px; margin-bottom: 6px; }
.sm-compat-score { font-family: 'Cinzel', serif; font-size: 46px; color: #FFDFA3; line-height: 1; }
.sm-compat-score span { font-size: 18px; color: rgba(195,169,255,0.6); }
.sm-compat-overall-label { font-family: 'Cinzel', serif; letter-spacing: 0.14em; font-size: 11px; text-transform: uppercase; color: rgba(195,169,255,0.6); }
.sm-meters { display: flex; flex-direction: column; gap: 10px; margin: 16px 0 18px; }
.sm-meter-head { display: flex; justify-content: space-between; font-size: 12px; color: rgba(232,216,255,0.8); margin-bottom: 4px; }
.sm-meter-head b { color: #FFDFA3; }
.sm-meter-bar { height: 7px; border-radius: 999px; background: rgba(255,255,255,0.07); overflow: hidden; }
.sm-meter-fill { height: 100%; border-radius: 999px; background: linear-gradient(90deg,#8a5fd6,#FFDFA3); }
/* About sub-tabs (Detail | Compatibility) — sit between the identity rule and a second rule. */
.sm-about-subtabs { display: flex; gap: 6px; border-bottom: 1px solid rgba(93,59,140,0.25); margin: 0 0 16px; padding-top: 14px; }
.sm-compat-sub { display: flex; gap: 6px; border-bottom: 1px solid rgba(93,59,140,0.25); margin-bottom: 14px; }
.sm-subtab { background: none; border: none; border-bottom: 2px solid transparent; color: rgba(195,169,255,0.5); font-family: 'Cinzel', serif; font-size: 13px; padding: 4px 2px 10px; margin-right: 16px; cursor: pointer; }
.sm-subtab.active { color: #FFDFA3; border-bottom-color: #FFDFA3; }
.sm-compat-report { font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 16px; color: rgba(232,216,255,0.88); line-height: 1.6; margin: 0; }
.sm-aspects { display: flex; flex-direction: column; gap: 7px; }
.sm-aspect { display: flex; align-items: center; gap: 10px; padding: 8px 12px; border-radius: 10px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.06); font-size: 13px; color: rgba(232,216,255,0.8); }
.sm-aspect.harmonious { border-left: 3px solid #7ad6a0; }
.sm-aspect.challenging { border-left: 3px solid #e08fb0; }
.sm-aspect-glyph { font-size: 16px; color: #FFDFA3; width: 18px; text-align: center; flex: none; }
.sm-aspect-text { flex: 1; }
.sm-aspect-text b { color: #fff; font-weight: 600; }
.sm-aspect-orb { font-size: 11px; color: rgba(195,169,255,0.5); flex: none; }

/* Sun & Rising on the public profile — side by side, equal size. */
.sm-signs { display: flex; gap: 14px; flex-wrap: wrap; margin-top: 18px; }
.sm-sign-card { display: flex; align-items: center; gap: 14px; flex: 1 1 0; min-width: 220px;
  background: linear-gradient(160deg, rgba(93,59,140,0.16), rgba(28,16,48,0.3));
  border: 1px solid rgba(240,217,163,0.2); border-radius: 16px; padding: 14px 16px; }
.sm-sign-card.is-sun { border-color: rgba(240,217,163,0.32); }
.sm-sign-visual { flex: none; width: 80px; height: 80px; border-radius: 14px; padding: 0; overflow: hidden;
  display: flex; align-items: center; justify-content: center;
  border: 1px solid rgba(240,217,163,0.25); background: rgba(20,8,40,0.5); }
.sm-sign-visual.is-img { cursor: zoom-in; }
.sm-sign-visual img { width: 100%; height: 100%; object-fit: cover; display: block; }
.sm-glyph { font-size: 42px; color: #f6e7b8; line-height: 1; }
.sm-sign-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.sm-sign-name { font-family: 'Cinzel', serif; font-size: 20px; color: #f5e8c0; }
.sm-sign-role { font-size: 11px; color: #b9a9d8; letter-spacing: 0.1em; text-transform: uppercase; }

/* Mirror sections */
.sm-mirror-block { margin-top: 22px; }
.sm-section-label { display: block; font-family: 'Cinzel', serif; letter-spacing: 0.18em; font-size: 10px; text-transform: uppercase; color: rgba(255,223,163,0.7); margin-bottom: 10px; }
.sm-affirmation { font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 17px; color: rgba(232,216,255,0.85); line-height: 1.5; margin: 0; }
.sm-altar-img { width: 100%; max-height: 320px; object-fit: cover; border-radius: 12px; cursor: pointer; display: block; border: 1px solid rgba(195,169,255,0.2); }
/* Where the signs meet: a note under the sign cards, as on the seeker's own Mirror. */
.sm-signs-about { margin: 14px 0 0; padding: 12px 14px; font-size: 14px; line-height: 1.6; color: #e4d8ff;
  background: linear-gradient(160deg, rgba(93,59,140,0.16), rgba(28,16,48,0.28));
  border: 1px solid rgba(240,217,163,0.12); border-left: 2px solid rgba(240,217,163,0.34); border-radius: 10px; }

.seeker-img-overlay { position: fixed; inset: 0; z-index: 9100; background: rgba(0,0,0,0.92); display: flex; align-items: center; justify-content: center; cursor: pointer; padding: 24px; }
.seeker-img-overlay img { max-width: 100%; max-height: 100%; object-fit: contain; border-radius: 12px; }

.seeker-modal-fade-enter-active, .seeker-modal-fade-leave-active { transition: opacity 0.2s; }
.seeker-modal-fade-enter-from, .seeker-modal-fade-leave-to { opacity: 0; }

/* Phone: prevent horizontal overflow on narrow screens */
@media (max-width: 480px) {
  .sm-tabs { padding: 14px 14px 0; }
  .sm-body { padding: 16px 14px; }
  .sm-reading-actions { flex-wrap: wrap; }
  .sm-signs { flex-direction: column; }
  .sm-sign-card { min-width: 0; }
}
</style>
